import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';

// This component provides an interface for users to upload and crop images before finalizing the upload

interface ImageCropperProps {
  onCropComplete: (croppedImageFile: File) => void;
  onCancel: () => void;
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

// Main ImageCropper component
const ImageCropper: React.FC<ImageCropperProps> = ({ onCropComplete, onCancel }) => {
  const [imgSrc, setImgSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement>(null);
  const [fileName, setFileName] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState(false);

  // Process the selected file and read it as a data URL
  const processFile = (file: File) => {
    setFileName(file.name);
    setCrop(undefined); // Makes crop preview update between images.
    const reader = new FileReader();
    reader.addEventListener('load', () =>
      setImgSrc(reader.result?.toString() || ''),
    );
    reader.readAsDataURL(file);
  };

  // Handle file selection via input
  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  /// Handle drag over event
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  // Handle drag leave event
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  // Handle drop event
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      processFile(imageFile);
    }
  };

  // When the image loads, set an initial crop
  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    // Square aspect ratio (1:1)
    setCrop(centerAspectCrop(width, height, 1));
  };

  // Generate the cropped image file
  const getCroppedImg = useCallback(async (): Promise<File | null> => {
    // an empty crop would make drawImage throw, so treat it as "nothing to crop"
    if (!completedCrop?.width || !completedCrop?.height || !imgRef.current) return null;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    // Use higher resolution output to avoid blurry backgrounds on group cards
    const targetSize = 256;
    canvas.width = targetSize;
    canvas.height = targetSize;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Calculate scale factors
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Draw the cropped and resized image
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      targetSize,
      targetSize,
    );

    // Convert canvas to blob and then to File
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        
        // Create a new File from the blob
        const fileExtension = fileName.split('.').pop() || 'jpg';
        const newFileName = `asset_${Date.now()}.${fileExtension}`;
        const croppedFile = new File([blob], newFileName, {
          type: blob.type || 'image/jpeg',
          lastModified: Date.now(),
        });
        
        resolve(croppedFile);
      }, 'image/jpeg', 0.90);
    });
  }, [completedCrop, fileName]);

  // Handle crop completion and return the cropped image file
  const handleCropComplete = async () => {
    const croppedFile = await getCroppedImg();
    if (croppedFile) {
      onCropComplete(croppedFile);
    }
  };

  // Reset the image and crop state
  const resetImage = () => {
    setImgSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
    setFileName('');
  };

  return (
    <div className="flex flex-col gap-4">
      {!imgSrc && (
        <div>
          <label className="eai-meta-label mb-2 block">Select Image</label>
          <div
            className={`relative rounded-eai-card border-2 border-dashed p-8 text-center transition-colors ${
              isDragOver
                ? 'border-eai-cta-primary bg-eai-surface-hover'
                : 'border-eai-border hover:border-eai-border-hover hover:bg-eai-surface-sunken'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="image/*"
              onChange={onSelectFile}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            />
            <svg
              className="mx-auto h-10 w-10 text-eai-ink-ghost"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden
            >
              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p className="mt-3 font-inter text-eai-body-sm text-eai-ink-subtle">
              <span className="font-medium text-eai-cta-primary">Click to upload</span> or drag and drop
            </p>
            <p className="mt-1 font-inter text-eai-body-xs text-eai-ink-faint">
              PNG, JPG up to 10MB
            </p>
          </div>
        </div>
      )}

      {imgSrc && (
        <div className="flex flex-col gap-4">
          <div>
            <label className="eai-meta-label mb-2 block">Crop to Square</label>
            <div className="flex justify-center">
              <div className="eai-card inline-block max-w-[min(500px,100%)] overflow-hidden p-3">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1} // Square aspect ratio
                  keepSelection // a tap outside the box would otherwise wipe the crop and start a 0x0 one — painful on touch
                  minWidth={50}
                  minHeight={50}
                  className="block max-h-80" // the library chains max-height down to the img, so it has to live here not on the img
                >
                  <img
                    ref={imgRef}
                    alt="Crop me"
                    src={imgSrc}
                    onLoad={onImageLoad}
                    className="block h-auto"
                  />
                </ReactCrop>
              </div>
            </div>
          </div>

          {/* Stacks below sm so three 40px pills don't shrink into each other on a phone */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button type="button" onClick={resetImage} className="eai-btn-outline sm:mr-auto">
              Change Image
            </button>
            <div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
              <button type="button" onClick={onCancel} className="eai-btn-outline">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropComplete}
                disabled={!completedCrop?.width || !completedCrop?.height}
                className="eai-btn-cta"
              >
                Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageCropper;
